<template>
  <model-table
    ref="table"
    :collection="thirdPartyTransactions"
    :columns="columns"
    :show-add="false"
    :dialog="isDialog"
  >
    <!-- Expandable Row for Payloads -->
    <template #body="props">
      <q-tr :props="props">
        <q-td
          key="expand"
          :props="props"
        >
          <q-btn
            size="sm"
            color="primary"
            round
            dense
            :icon="props.expand ? 'expand_less' : 'expand_more'"
            @click="props.expand = !props.expand"
          />
        </q-td>
        <q-td
          key="id"
          :props="props"
        >
          {{ props.row.id }}
        </q-td>
        <q-td
          key="providerName"
          :props="props"
        >
          <div class="row items-center q-gutter-xs">
            <q-icon
              name="business"
              color="primary"
              size="sm"
            />
            <span class="text-weight-medium">
              {{ props.row.providerName }}
            </span>
          </div>
        </q-td>
        <q-td
          key="operationType"
          :props="props"
        >
          <q-chip
            :color="getOperationTypeColor(props.row.operationType)"
            text-color="white"
            size="sm"
            :icon="getOperationTypeIcon(props.row.operationType)"
          >
            {{ props.row.operationType }}
          </q-chip>
        </q-td>
        <q-td
          key="status"
          :props="props"
        >
          <q-chip
            :color="getStatusColor(props.row.status)"
            text-color="white"
            size="sm"
            :icon="getStatusIcon(props.row.status)"
          >
            {{ props.row.status }}
          </q-chip>
        </q-td>
        <q-td
          key="errorMessage"
          :props="props"
          style="max-width: 200px; overflow: hidden;"
        >
          <div v-if="props.row.errorMessage">
            <q-icon
              name="error"
              color="negative"
              size="sm"
              class="q-mr-xs"
            />
            <span
              class="text-negative text-caption"
              style="display: block; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;"
              :title="props.row.errorMessage"
            >
              {{ props.row.errorMessage }}
            </span>
          </div>
          <span
            v-else
            class="text-grey-5 text-caption"
          >
            No errors
          </span>
        </q-td>
        <q-td
          key="createdAt"
          :props="props"
        >
          <formatted-date
            :value="props.row.createdAt"
            format="timestamp"
          />
        </q-td>
        <q-td
          key="updatedAt"
          :props="props"
        >
          <formatted-date
            :value="props.row.updatedAt"
            format="timestamp"
          />
        </q-td>
      </q-tr>
      <q-tr
        v-show="props.expand"
        :props="props"
      >
        <q-td colspan="100%">
          <div class="q-pa-md">
            <div class="row q-col-gutter-md">
              <!-- Request Payload -->
              <div class="col-12 col-md-6">
                <div class="text-subtitle2 text-weight-medium q-mb-sm">
                  <q-icon
                    name="send"
                    class="q-mr-xs"
                  />
                  Request Payload
                </div>
                <q-card
                  flat
                  bordered
                  class="bg-grey-1"
                >
                  <q-card-section class="q-pa-sm">
                    <pre class="text-caption">{{ formatPayload(props.row.requestPayload) }}</pre>
                  </q-card-section>
                </q-card>
              </div>

              <!-- Response Payload -->
              <div class="col-12 col-md-6">
                <div class="text-subtitle2 text-weight-medium q-mb-sm">
                  <q-icon
                    name="reply"
                    class="q-mr-xs"
                  />
                  Response Payload
                </div>
                <q-card
                  flat
                  bordered
                  class="bg-grey-1"
                >
                  <q-card-section class="q-pa-sm">
                    <pre class="text-caption">{{ formatPayload(props.row.responsePayload) }}</pre>
                  </q-card-section>
                </q-card>
              </div>
            </div>
          </div>
        </q-td>
      </q-tr>
    </template>
  </model-table>
</template>

<script>

import FormattedDate from 'src/modules/admin/components/FormattedDate'
import ModelTable from 'src/modules/admin/components/ModelTable'

export default {
  name: 'ThirdPartyTransactionTable',
  components: {
    FormattedDate,
    ModelTable
  },
  props: {
    rewardType: {
      type: String,
      required: false,
      default: null
    },
    rewardId: {
      type: Number,
      required: false,
      default: null
    },
    isDialog: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  computed: {
    columns () {
      return [
        {
          name: 'expand',
          label: '',
          field: 'expand',
          align: 'left',
          sortable: false,
          style: 'width: 50px'
        },
        {
          name: 'id',
          required: true,
          label: 'ID',
          align: 'left',
          field: 'id',
          sortable: true,
          style: 'width: 80px'
        },
        {
          name: 'providerName',
          label: 'Provider',
          align: 'left',
          field: 'providerName',
          sortable: true
        },
        {
          name: 'operationType',
          label: 'Operation',
          align: 'left',
          field: 'operationType',
          sortable: true
        },
        {
          name: 'status',
          label: 'Status',
          align: 'left',
          field: 'status',
          sortable: true
        },
        {
          name: 'errorMessage',
          label: 'Error',
          align: 'left',
          field: 'errorMessage',
          sortable: false,
          style: 'max-width: 200px'
        },
        {
          name: 'createdAt',
          label: 'Created',
          align: 'left',
          field: 'createdAt',
          sortable: true,
          style: 'width: 150px'
        },
        {
          name: 'updatedAt',
          label: 'Updated',
          align: 'left',
          field: 'updatedAt',
          sortable: true,
          style: 'width: 150px'
        }
      ]
    }
  },
  models: {
    thirdPartyTransactions: {
      variables () {
        const variables = {}
        if (this.rewardType) {
          variables.rewardType = this.rewardType
        }
        if (this.rewardId) {
          variables.rewardId = this.rewardId
        }
        return variables
      }
    }
  },
  methods: {
    getOperationTypeColor (operationType) {
      const colors = {
        reward: 'positive',
        redeem: 'info',
        balance: 'primary',
        transfer: 'warning',
        refund: 'negative'
      }
      return colors[operationType?.toLowerCase()] || 'grey'
    },
    getOperationTypeIcon (operationType) {
      const icons = {
        reward: 'add_circle',
        redeem: 'remove_circle',
        balance: 'account_balance',
        transfer: 'swap_horiz',
        refund: 'undo'
      }
      return icons[operationType?.toLowerCase()] || 'help'
    },
    getStatusColor (status) {
      const colors = {
        success: 'positive',
        completed: 'positive',
        pending: 'warning',
        processing: 'info',
        failed: 'negative',
        error: 'negative',
        cancelled: 'grey'
      }
      return colors[status?.toLowerCase()] || 'grey'
    },
    getStatusIcon (status) {
      const icons = {
        success: 'check_circle',
        completed: 'check_circle',
        pending: 'schedule',
        processing: 'sync',
        failed: 'error',
        error: 'error',
        cancelled: 'cancel'
      }
      return icons[status?.toLowerCase()] || 'help'
    },
    show () {
      this.$refs.table.show()
    },
    formatPayload (payload) {
      if (!payload) return 'No data'

      try {
        // Try to parse as JSON and format it
        const parsed = JSON.parse(payload)
        return JSON.stringify(parsed, null, 2)
      } catch {
        // If not JSON, return as is
        return payload
      }
    }
  }
}
</script>
